import { useEffect, useRef, useState } from 'react';
import { Check, Phone, Video } from 'lucide-react';
import { prefersReducedMotion } from '../hooks';

type StepKind = 'in' | 'out' | 'typing' | 'note';

interface Step {
  kind: StepKind;
  text?: string;
  time?: string;
}

/** One WhatsApp thread, handled end to end by the agent. */
const STEPS: Step[] = [
  { kind: 'in', text: 'Do you have the SPX-200 infusion pump in stock?', time: '02:14' },
  { kind: 'typing' },
  { kind: 'out', text: 'In stock — 12 units. Want a formal quote?', time: '02:14' },
  { kind: 'note', text: 'tagged contact · warm-lead' },
  { kind: 'in', text: 'Yes please, for 20 units', time: '02:15' },
  { kind: 'typing' },
  { kind: 'out', text: 'Quote #Q-4471 sent — valid 30 days.', time: '02:15' },
  { kind: 'note', text: 'drafted in Lumen ERP · follow-up set' },
];

export function PhoneAgent(): React.JSX.Element {
  const isStatic = prefersReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(isStatic ? STEPS.length : 0);
  const [started, setStarted] = useState(isStatic);

  useEffect(() => {
    if (started) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started || isStatic || shown >= STEPS.length) return;
    const kind = STEPS[shown]?.kind;
    const delay = shown === 0 ? 500 : kind === 'typing' ? 500 : kind === 'note' ? 550 : 1150;
    const t = window.setTimeout(() => setShown((n) => n + 1), delay);
    return () => window.clearTimeout(t);
  }, [started, isStatic, shown]);

  // A typing indicator only lingers while it is the newest thing on screen.
  const visible = STEPS.slice(0, shown).filter(
    (s, i) => !(s.kind === 'typing' && (isStatic || i < shown - 1)),
  );

  return (
    <div
      className="phone"
      ref={ref}
      role="img"
      aria-label="A WhatsApp conversation handled by an Inafana agent: it confirms stock for an SPX-200 pump, tags the contact as a warm lead, sends quote Q-4471 drafted in Lumen ERP, and schedules a follow-up."
    >
      <div className="phone-frame" aria-hidden="true">
        <span className="phone-notch" />
        <div className="phone-screen">
          <div className="phone-head">
            <span className="phone-avatar">IN</span>
            <span className="phone-who">
              <b>Inafana</b>
              <em>agent · online</em>
            </span>
            <span className="phone-icons">
              <Video size={13} />
              <Phone size={13} />
            </span>
          </div>

          <div className="phone-chat">
            {visible.map((step, i) =>
              step.kind === 'typing' ? (
                <div key={`t-${i}`} className="bubble typing">
                  <span />
                  <span />
                  <span />
                </div>
              ) : step.kind === 'note' ? (
                <div key={`n-${i}`} className="phone-note">
                  <Check size={11} strokeWidth={3} />
                  {step.text}
                </div>
              ) : (
                <div key={`m-${i}`} className={`bubble ${step.kind}`}>
                  {step.text}
                  <span className="bubble-meta">
                    {step.time}
                    {step.kind === 'out' && <b className="ticks">✓✓</b>}
                  </span>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
